import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span>Go Back</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-serif text-white mb-4">Terms of Service</h1>
          <p className="text-zinc-400">Last Updated: April 2026</p>
        </div>

        <div className="space-y-10 text-zinc-300">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="mb-4">
              Welcome to 100Nook ("100Nook," "Platform," "we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of the 100Nook website, applications, and services.
            </p>
            <p className="mb-4">
              100Nook is a marketplace platform that enables users to list, discover, and book private spaces including but not limited to home gyms, tennis courts, pools, backyards, studios, and other privately owned amenities.
            </p>
            <p className="text-zinc-400">
              By accessing or using 100Nook, you agree to be bound by these Terms.
            </p>
          </section>

          <hr className="border-white/10" />

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Marketplace Model</h2>
            <p className="mb-4">100Nook acts solely as a marketplace platform that connects:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Hosts offering private spaces</li>
              <li>Guests booking private spaces</li>
              <li>Affiliates referring hosts</li>
            </ul>
            <p className="mb-4 font-semibold text-white">100Nook does NOT:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Own properties</li>
              <li>Operate properties</li>
              <li>Control host behavior</li>
              <li>Guarantee safety of properties</li>
            </ul>
            <p className="text-zinc-400">
              Hosts and Guests contract directly with each other. 100Nook is not a party to any agreement between Hosts and Guests.
            </p>
          </section>

          <hr className="border-white/10" />

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Eligibility</h2>
            <p className="mb-4">To use 100Nook, you must:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Be at least 18 years old</li>
              <li>Provide accurate information</li>
              <li>Comply with local laws</li>
              <li>Have authority to enter into agreements</li>
            </ul>
            <p className="text-zinc-400">100Nook reserves the right to refuse service.</p>
          </section>

          <hr className="border-white/10" />

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. User Accounts</h2>
            <p className="mb-4">You agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Maintain accurate information</li>
              <li>Protect your login credentials</li>
              <li>Be responsible for activity under your account</li>
            </ul>
            <p className="text-zinc-400">100Nook may suspend accounts at its sole discretion.</p>
          </section>

          <hr className="border-white/10" />

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Host Terms</h2>
            <p className="mb-4">Hosts agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Provide accurate listing details</li>
              <li>Maintain safe premises</li>
              <li>Follow local zoning rules</li>
              <li>Maintain appropriate insurance</li>
              <li>Honor confirmed bookings</li>
            </ul>
            <p className="mb-4 font-semibold text-white">Hosts are solely responsible for:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Property safety</li>
              <li>Guest safety</li>
              <li>Permits and licenses</li>
              <li>Local compliance</li>
            </ul>
            <p className="text-zinc-400">100Nook assumes no responsibility for host listings.</p>
          </section>

          <hr className="border-white/10" />

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Guest Terms</h2>
            <p className="mb-4">Guests agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Respect host property</li>
              <li>Follow host rules</li>
              <li>Avoid illegal activity</li>
              <li>Pay for damages</li>
            </ul>
            <p className="mb-4 font-semibold text-white">Guests are responsible for:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400">
              <li>Damage</li>
              <li>Injury caused</li>
              <li>Rule violations</li>
            </ul>
          </section>

          <hr className="border-white/10" />

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Booking Terms</h2>
            <p className="mb-4">When a booking is confirmed:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Guest enters agreement with Host</li>
              <li>Host agrees to provide access</li>
              <li>100Nook facilitates payment</li>
            </ul>
            <p className="mb-4 font-semibold text-white">Hosts control:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400">
              <li>Pricing</li>
              <li>Availability</li>
              <li>House rules</li>
            </ul>
          </section>

          <hr className="border-white/10" />

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Payments & Fees</h2>
            <p className="mb-4 text-zinc-400">100Nook uses third-party payment providers including Stripe.</p>
            <p className="mb-4 font-semibold text-white">100Nook may:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Charge platform fees</li>
              <li>Deduct commissions</li>
              <li>Hold funds for risk review</li>
              <li>Issue refunds</li>
            </ul>
            <p className="mb-4 font-semibold text-white">Payouts to hosts may be:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400">
              <li>Scheduled</li>
              <li>Delayed</li>
              <li>Adjusted</li>
            </ul>
          </section>

          <hr className="border-white/10" />

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Cancellations</h2>
            <p className="mb-4 text-zinc-400">Hosts may define cancellation policies.</p>
            <p className="mb-4 font-semibold text-white">100Nook may:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400">
              <li>Cancel bookings</li>
              <li>Issue refunds</li>
              <li>Resolve disputes</li>
            </ul>
          </section>

          <hr className="border-white/10" />

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Affiliate Program</h2>
            <p className="mb-4 text-zinc-400">100Nook offers affiliate commissions.</p>
            <p className="mb-4 font-semibold text-white">Affiliate terms:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Earn per completed booking</li>
              <li>Subject to fraud review</li>
              <li>Subject to policy compliance</li>
            </ul>
            <p className="mb-4 font-semibold text-white">100Nook may:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400">
              <li>Modify commissions</li>
              <li>Suspend affiliates</li>
              <li>Audit referrals</li>
            </ul>
          </section>

          <hr className="border-white/10" />

          {/* Section 11 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Risk Assumption</h2>
            <p className="mb-4">Users acknowledge that:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Private spaces carry risk</li>
              <li>Physical activity carries risk</li>
              <li>100Nook does not inspect properties</li>
            </ul>
            <p className="text-zinc-400 font-semibold">Users assume all risk.</p>
          </section>

          <hr className="border-white/10" />

          {/* Section 12 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">12. Limitation of Liability</h2>
            <p className="mb-4 text-zinc-400">To the maximum extent permitted by law:</p>
            <p className="mb-4 font-semibold text-white">100Nook shall not be liable for:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Injury</li>
              <li>Property damage</li>
              <li>Disputes</li>
              <li>Loss of income</li>
            </ul>
            <p className="text-zinc-400">Maximum liability limited to fees paid.</p>
          </section>

          <hr className="border-white/10" />

          {/* Section 13 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">13. Indemnification</h2>
            <p className="mb-4 text-zinc-400">
              Users agree to indemnify, defend, and hold harmless 100Nook, its affiliates, subsidiaries, parent companies, successors, assigns, officers, directors, employees, agents, partners, and service providers from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400">
              <li>Bookings or reservations made through the platform</li>
              <li>Use of any property, space, or amenities</li>
              <li>User conduct or interactions with other users</li>
              <li>Violation of these Terms or applicable laws</li>
              <li>Damage to property or personal injury related to platform use</li>
            </ul>
          </section>

          <hr className="border-white/10" />

          {/* Section 14 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">14. Termination</h2>
            <p className="mb-4 font-semibold text-white">100Nook may suspend:</p>
            <ul className="list-disc list-inside space-y-2 text-zinc-400 mb-4">
              <li>Accounts</li>
              <li>Listings</li>
              <li>Affiliates</li>
            </ul>
            <p className="text-zinc-400">At its discretion.</p>
          </section>

          <hr className="border-white/10" />

          {/* Section 15 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">15. Governing Law</h2>
            <p className="text-zinc-400">
              These Terms governed by: <span className="text-white">United States, State of Maryland</span>
            </p>
          </section>

          <hr className="border-white/10" />

          {/* Section 16 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">16. Contact</h2>
            <p className="text-zinc-400">
              <a href="mailto:admin@100nook.com" className="text-[#D4AF37] hover:underline">
                admin@100nook.com
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
